import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { PanelProduct } from '@storeo/api-client';
import { api } from '../lib/api';
import shellStyles from '../components/shell/shell.module.css';
import ui from '../styles/ui.module.css';

const TYPE_LABEL: Record<string, string> = {
  one_time: 'Pagamento único',
  subscription: 'Assinatura mensal',
};

const CHANNEL_LABEL: Record<string, string> = {
  digital_file: 'Arquivo para download',
  digital_link: 'Link ou instrução',
  video: 'Vídeo (área do cliente)',
  telegram: 'Acesso a grupo do Telegram',
  physical: 'Produto físico (eu envio)',
};

function money(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function Produtos() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const products = useQuery({ queryKey: ['products'], queryFn: () => api.products() });
  const editingProduct = products.data?.find((p) => p.id === editingId) ?? null;

  const togglePublish = useMutation({
    mutationFn: (p: PanelProduct) => (p.status === 'published' ? api.unpublishProduct(p.id) : api.publishProduct(p.id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });

  return (
    <>
      <div className={shellStyles.head}>
        <h1>Produtos</h1>
        <p>O que você vende — preço, tipo e como entrega.</p>
      </div>

      <div className={ui.actions}>
        <button
          className={`${ui.btn} ${ui.btnPrimary}`}
          onClick={() => {
            setCreating((v) => !v);
            setEditingId(null);
          }}
        >
          {creating ? 'Fechar' : '+ Novo produto'}
        </button>
      </div>

      {creating ? (
        <div className={ui.card} style={{ padding: '18px 20px' }}>
          <ProductForm mode="create" onDone={() => setCreating(false)} />
        </div>
      ) : null}

      {editingProduct ? (
        <div className={ui.card} style={{ padding: '18px 20px' }}>
          <ProductForm mode="edit" product={editingProduct} onDone={() => setEditingId(null)} />
        </div>
      ) : null}

      <section>
        <div className={ui.secH}>
          <h2>Todos os produtos</h2>
        </div>
        <div className={ui.card}>
          {products.isLoading ? (
            <p className={ui.empty}>Carregando…</p>
          ) : !products.data?.length ? (
            <p className={ui.empty}>Nenhum produto ainda — crie o primeiro acima.</p>
          ) : (
            <div className={ui.tblScroll}>
              <table className={ui.tbl}>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Tipo</th>
                    <th>Canal</th>
                    <th className="r">Preço</th>
                    <th>Status</th>
                    <th className="r">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {products.data.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <b>{p.name}</b>
                        <div className={ui.faint}>/{p.slug}</div>
                      </td>
                      <td className={ui.muted}>{TYPE_LABEL[p.type] ?? p.type}</td>
                      <td className={ui.muted}>{CHANNEL_LABEL[p.deliveryChannel] ?? p.deliveryChannel}</td>
                      <td className="r">
                        <span className={ui.money}>{money(p.amount)}</span>
                      </td>
                      <td>
                        <span className={`${ui.pill} ${p.status === 'published' ? ui.pillPos : ui.pillMuted}`}>
                          {p.status === 'published' ? 'Publicado' : 'Rascunho'}
                        </span>
                      </td>
                      <td className="r">
                        <div className={ui.actions} style={{ justifyContent: 'flex-end' }}>
                          <button
                            className={`${ui.btn} ${ui.btnGhost} ${ui.btnSmall}`}
                            onClick={() => {
                              setEditingId(p.id);
                              setCreating(false);
                            }}
                          >
                            Editar
                          </button>
                          <Link className={`${ui.btn} ${ui.btnGhost} ${ui.btnSmall}`} to={`/produtos/${p.id}`}>
                            Entrega
                          </Link>
                          <button
                            className={`${ui.btn} ${ui.btnGhost} ${ui.btnSmall}`}
                            disabled={togglePublish.isPending}
                            onClick={() => togglePublish.mutate(p)}
                          >
                            {p.status === 'published' ? 'Despublicar' : 'Publicar'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

// combining marks (U+0300–U+036F) que sobram depois do normalize('NFD') — remove os acentos
const DIACRITICS = /[̀-ͯ]/g;

function slugify(v: string): string {
  return v
    .normalize('NFD')
    .replace(DIACRITICS, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

type ProductFormProps =
  | { mode: 'create'; onDone: () => void }
  | { mode: 'edit'; product: PanelProduct; onDone: () => void };

/** Nome/preço/descrição/tipo/canal — o mesmo form serve pra criar e pra editar (o slug, uma
 *  vez publicado, é a URL pública; não muda depois de criado). */
function ProductForm(props: ProductFormProps) {
  const qc = useQueryClient();
  const editing = props.mode === 'edit' ? props.product : null;
  const [name, setName] = useState(editing?.name ?? '');
  const [slug, setSlug] = useState(editing?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState(editing?.description ?? '');
  const [type, setType] = useState(editing?.type ?? 'one_time');
  const [amount, setAmount] = useState(editing ? String(editing.amount) : '');
  const [deliveryChannel, setDeliveryChannel] = useState(editing?.deliveryChannel ?? 'digital_file');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const details = {
        name,
        description: description || undefined,
        type,
        amount: Number(amount.replace(',', '.')),
        deliveryChannel,
      };
      if (props.mode === 'edit') {
        await api.updateProduct(props.product.id, details);
      } else {
        await api.createProduct({ ...details, slug });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
      props.onDone();
    },
    onError: (e: unknown) =>
      setError(e instanceof Error ? e.message : `Não foi possível ${props.mode === 'edit' ? 'salvar' : 'criar'} o produto`),
  });

  return (
    <form
      className={ui.form}
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        save.mutate();
      }}
    >
      <label className={ui.field}>
        Nome
        <input
          required
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (props.mode === 'create' && !slugTouched) setSlug(slugify(e.target.value));
          }}
        />
      </label>
      {props.mode === 'create' ? (
        <label className={ui.field}>
          Endereço (slug)
          <input
            required
            value={slug}
            onChange={(e) => {
              setSlug(slugify(e.target.value));
              setSlugTouched(true);
            }}
          />
          <span className={ui.hint}>Aparece na URL da sua loja: /p/{slug || 'seu-produto'}</span>
        </label>
      ) : (
        <p className={ui.hint}>Endereço: /p/{slug} — não muda depois de criado.</p>
      )}
      <label className={ui.field}>
        Descrição
        <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <div className={ui.formRow}>
        <label className={ui.field}>
          Cobrança
          <select value={type} onChange={(e) => setType(e.target.value)}>
            {Object.entries(TYPE_LABEL).map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className={ui.field}>
          Preço (R$)
          <input required inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </label>
      </div>
      <label className={ui.field}>
        Como o cliente recebe?
        <select value={deliveryChannel} onChange={(e) => setDeliveryChannel(e.target.value)}>
          {Object.entries(CHANNEL_LABEL).map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </select>
      </label>
      {error ? <p className={ui.error}>{error}</p> : null}
      <div className={ui.actions}>
        <button className={`${ui.btn} ${ui.btnPrimary}`} type="submit" disabled={save.isPending}>
          {save.isPending ? 'Salvando…' : props.mode === 'edit' ? 'Salvar alterações' : 'Criar produto'}
        </button>
      </div>
    </form>
  );
}
